// Typ: FindAnnulusAreaFromRadiiTask
// Temat: Okrąg i koło → Elementy, długości i pola → Pole pierścienia
// Wzór:  P = π (R^2 − r^2), gdzie R > r

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import PiInput, { parsePiLikeInput, formatPiValueLatex } from "../../../../components/inputs/PiInput";

const DEC_TOL = 0.1;

function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// zapis k·π jako ułamek mieszany (jeśli potrzeba)
function formatMixedTimesPi(n, d) {
  const q = Math.floor(n / d);
  const r = n % d;
  if (r === 0) return q === 1 ? "\\pi" : `${q}\\,\\pi`;
  if (q === 0)  return `\\dfrac{${r}}{${d}}\\,\\pi`;
  return `${q}\\,\\dfrac{${r}}{${d}}\\,\\pi`;
}

// ===== generator =====
export function generateTask() {
  // promienie całkowite 2..12, zapewniamy R > r
  const pick = () => Math.floor(Math.random() * 11) + 2; // 2..12
  let r = pick(), R = pick();
  if (r === R) R += 1;
  if (r > R) [r, R] = [R, r];

  return { type: "FindAnnulusAreaFromRadiiTask", data: { r, R } };
}

// ===== render task =====
export function renderTask({ data }) {
  const { r, R } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dane są dwa współśrodkowe okręgi o promieniach
      {" "}<InlineMath math={`r=${r}`} /> (wewnętrzny) oraz{" "}
      <InlineMath math={`R=${R}`} /> (zewnętrzny). Tworzą pierścień.
      Oblicz jego pole <InlineMath math={"P"} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <PiInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={"P"}
      placeholder="np. 25π, 9/2π, 1 1/3π lub 78.5 (±0.1)"
    />
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { r, R } = data;
  const expected = Math.PI * (R * R - r * r);

  const parsed =
    typeof userAnswer === "object" && userAnswer?.parsed != null
      ? userAnswer.parsed
      : parsePiLikeInput(typeof userAnswer === "object" ? userAnswer.raw : userAnswer);

  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - expected) <= DEC_TOL;
}

// ===== exact answer =====
export function renderAnswer({ data }) {
  const { r, R } = data;
  const num0 = R * R - r * r; // (R^2 − r^2)
  const den0 = 1;
  const g = gcd(num0, den0);
  const n = num0 / g, d = den0 / g;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${formatMixedTimesPi(n, d)}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { r, R } = data;
  const exactK = R * R - r * r;
  const approx = (Math.PI * exactK).toFixed(2);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór na pole pierścienia:</div>
      <BlockMath math={'P = \\pi\\,(R^{2}-r^{2})'} />

      <div><strong>Krok 2.</strong> Podstawiamy <InlineMath math={`r=${r}`} /> i <InlineMath math={`R=${R}`} />:</div>
      <BlockMath math={`P = \\pi\\,(${R}^{2}-${r}^{2}) = ${exactK}\\,\\pi`} />

      <div><strong>Krok 3.</strong> (Opcjonalnie) Przybliżenie:</div>
      <BlockMath math={`P \\approx ${approx}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${formatPiValueLatex(raw) || ""}`} />
    </div>
  );
}
